import React, {useState, useRef, useEffect} from 'react';
import Icon from '@/components/icons/Icon';
import LogoIcon from '@/components/icons/LogoIcon';
import LogoIconWhite from '@/components/icons/LogoIconWhite';
import ChatHistory from './ChatHistory';
import ChatInterface from './ChatInterface';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import {ChartAskAIEvent, createChartMessage} from '@/utils/chartAskAI';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {isModuleEnabled} from '@/utils/modules';

type Message = {
    sender: 'user' | 'bot';
    text: string;
    timestamp: Date;
    isStreaming?: boolean;
    progress?: number;
    status?: string;
};

type Conversation = {
    id: number;
    hash: string;
    title?: string;
    updatedAt: string;
    messages?: Array<{
        id: number;
        role: string;
        content: string;
        createdAt: string;
    }>;
};

type PaginationData = {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
    next_num: number | null;
    prev_num: number | null;
};

type ConversationResponse = {
    conversations: Conversation[];
    pagination: PaginationData;
    search: string | null;
};

type ViewType = 'history' | 'chat';

const ChatbotModal: React.FC = () => {
    const {userData} = useOptimizedConfig();
    const moduleEnabled = isModuleEnabled(userData, 'addon-ask-ai');
    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const prevOpenRef = useRef(open);
    const [view, setView] = useState<ViewType>('chat');
    const [history, setHistory] = useState<Conversation[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [currentConversationHash, setCurrentConversationHash] = useState<string | null>(null);
    const [chatWidth, setChatWidth] = useState(400);
    const [isResizing, setIsResizing] = useState(false);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const chatContainerRef = useRef<HTMLDivElement>(null);

    const fetchConversations = (page: number = 1, search: string = '') => {
        setHistoryLoading(true);

        const params = new URLSearchParams({
            page: page.toString(),
            per_page: '10'
        });

        if (search.trim()) {
            params.append('search', search.trim());
        }

        const url = `${window.URLS?.getUserConversationsResource || '/internal_api/v1/get_user_conversations'}?${params}`;

        fetch(url)
            .then(res => res.json())
            .then((data: ConversationResponse) => {
                setHistory(data.conversations || []);
                setPagination(data.pagination || null);
            })
            .catch(() => {
                setHistory([]);
                setPagination(null);
            })
            .finally(() => setHistoryLoading(false));
    };

    useEffect(() => {
        if (view === 'history') {
            fetchConversations(currentPage, searchTerm);
        }
    }, [view, currentPage, searchTerm]);

    useEffect(() => {
        if (!prevOpenRef.current && open && !minimized) {
            // Only reset if not minimized
            setMessages([]);
            setCurrentConversationHash(null); // Clear hash when opening fresh
            setView('chat'); // Always start on chat view
        }
        prevOpenRef.current = open;
        // Reset minimized flag when chat is open
        if (open) {setMinimized(false);}
        
        // Add/remove body class for sidebar layout
        if (open) {
            document.body.classList.add('cb-sidebar-open');
            // Dispatch custom event for charts to resize
            window.dispatchEvent(new CustomEvent('chatSidebarOpened'));
        } else {
            document.body.classList.remove('cb-sidebar-open');
            // Dispatch custom event for charts to resize
            window.dispatchEvent(new CustomEvent('chatSidebarClosed'));
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('cb-sidebar-open');
        };
    }, [open]);

    // Listen for chart AskAI events
    useEffect(() => {
        const handleChartAskAI = (event: CustomEvent<ChartAskAIEvent>) => {
            const {chartId, chartTitle, message} = event.detail;

            // Open the chatbot
            setOpen(true);
            setView('chat');

            // Clear existing messages and start fresh
            setMessages([]);
            setCurrentConversationHash(null);

            // Dispatch event to ChatInterface to auto-send the chart message
            const chartMessage = message || createChartMessage(chartId, chartTitle);
            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('autoSendMessage', {
                    detail: {message: chartMessage}
                }));
            }, 200); // Small delay to ensure ChatInterface is ready
        };

        window.addEventListener('openAskAIWithChart', handleChartAskAI as EventListener);

        return () => {
            window.removeEventListener('openAskAIWithChart', handleChartAskAI as EventListener);
        };
    }, []);

    const handleNavigate = (targetView: 'chat', data?: {conversation?: Conversation, clearMessages?: boolean}) => {
        setView(targetView);

        if (data?.conversation) {

            const conversationMessages = data.conversation.messages?.map(msg => ({
                sender: msg.role === 'user' ? 'user' : 'bot' as 'user' | 'bot',
                text: msg.content,
                timestamp: new Date(msg.createdAt)
            })) || [];

            setMessages(conversationMessages);
            setCurrentConversationHash(data.conversation.hash);
        }

        if (data?.clearMessages) {
            setMessages([]);
            setCurrentConversationHash(null);
        }
    };

    const handleBackToHistory = () => {
        setView('history');
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (search: string) => {
        setSearchTerm(search);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleOpen = () => {
        if (!moduleEnabled) {
            return;
        }
        setOpen(true);
    };

    const handleMinimize = () => {
        setOpen(false);
        setMinimized(true);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (window.innerWidth <= 768) {return;}
        e.preventDefault();
        setIsResizing(true);
    };

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) {return;}
            
            const newWidth = window.innerWidth - e.clientX;
            const minWidth = 300;
            const maxWidth = Math.min(800, window.innerWidth * 0.8);
            
            if (newWidth >= minWidth && newWidth <= maxWidth) {
                setChatWidth(newWidth);
                
                const root = document.documentElement;
                root.style.setProperty('--chat-width', `${newWidth}px`);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--chat-width', `${chatWidth}px`);
    }, [chatWidth]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (window.innerWidth <= 768) {
                setChatWidth(window.innerWidth);
            } else if (chatWidth > window.innerWidth * 0.8) {
                setChatWidth(Math.min(400, window.innerWidth * 0.8));
            }
        };

        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [chatWidth]);

    return (
        <>
            {!open && (
                <button 
                    className="cb-open-button" 
                    onClick={handleOpen} 
                    data-cy="chatbot-window-removeeventlistener-btn"
                    style={{
                        opacity: moduleEnabled ? 1 : 0.5,
                        cursor: moduleEnabled ? 'pointer' : 'not-allowed'
                    }}
                    disabled={!moduleEnabled}
                    title={moduleEnabled ? i18n.t('jt_ask_ai.text') : 'Ask AI module is not enabled'}>
                    <div className="cb-open-button-logo">
                        <LogoIcon color='white' />
                    </div>
                </button>
            )}

            <div 
                className={`cb-chat-container ${!open ? 'cb-sidebar-hidden' : ''}`} 
                id="chat-form"
                ref={chatContainerRef}
                style={{width: `${chatWidth}px`}}
            >
                <div 
                    className="cb-resize-handle"
                    onMouseDown={handleMouseDown}
                />
                <div className="cb-chat-header cb-header-fixed">
                    <div className="cb-header-content">
                        {view !== 'history' && (
                            <button
                                className="cb-header-btn"
                                onClick={handleBackToHistory}
                                style={{marginRight: '5px'}}
                                data-cy="chatbot-btn-2">
                                <Icon name="mdi-chevron-left" size="1x" />
                            </button>
                        )}
                        <LogoIconWhite size={25} className="cb-logo-spacing" />
                        <h3>{i18n.t('jt_ask_ai.text')}</h3>
                    </div>

                    <div className="cb-buttons">
                        <button
                            className="cb-header-btn"
                            onClick={handleMinimize}
                            data-cy="chatbot-btn-1">
                            _
                        </button>

                        <button
                            className="cb-header-btn"
                            onClick={() => {setOpen(false); setMinimized(false);}}
                        >
                            <Icon name="mdi-close" size="1x" />
                        </button>
                    </div>
                </div>

                <div className="cb-content-area">

                    {view === 'chat' && (
                        <ChatInterface
                            messages={messages}
                            setMessages={setMessages}
                            conversationHash={currentConversationHash}
                        />
                    )}

                    {view === 'history' && (
                        <ChatHistory
                            history={history}
                            loading={historyLoading}
                            onNavigate={handleNavigate}
                            pagination={pagination}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            searchTerm={searchTerm}
                            onSearchChange={handleSearchChange}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default ChatbotModal; 